import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Container } from 'theme-ui';
import { IProductPremiumBlock } from '@features/cms/typescript/index';
import { IParsedImage } from '@components/typescript';
import VoxboxWidget from '@features/cms/components/collections/VoxboxWidget/VoxboxWidget';
import ProductPremiumPagination from '@features/cms/components/collections/ProductPremiumBlock/components/ProductPremiumPagination/ProductPremiumPagination';
import ProductPremiumSlide from '@features/cms/components/collections/ProductPremiumBlock/components/ProductPremiumSlide/ProductPremiumSlide';
import useResponsive from '@features/responsive/hooks/useResponsive';
import stackedStyles from '@features/cms/components/collections/ProductPremiumBlock/ProductPremiumStacked.styles';
import styles from '@features/cms/components/collections/ProductPremiumBlock/ProductPremiumBlock.styles';
type PropsType = {
  data: IProductPremiumBlock<IParsedImage>;
  isFirst?: boolean;
};
const getStickyBarHeight = () => {
  const stickyBar = document.querySelector('.navigationStickyBar');
  return stickyBar ? stickyBar.clientHeight : 0;
};
const ProductPremiumBlock = ({
  data,
  isFirst = false
}: PropsType) => {
  const SINGLE_SLIDER_HEIGHT = 400;
  const hoverRef = useRef<HTMLElement>(null);
  const [activeBlock, setActiveBlock] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [stickyBarHeight, setStickyBarHeight] = useState(0);
  // useResponsive already returns SSR fallback (isMobile: true) until hydrated — no extra delay.
  const {
    isMobile: isMobileSmall,
    isMobilelg
  } = useResponsive();
  const isMobile = Boolean(isMobileSmall || isMobilelg);
  const {
    blocks,
    code
  } = data;
  const hasViewport = viewportWidth > 0 && viewportHeight > 0;
  useLayoutEffect(() => {
    const updateViewport = () => {
      const {
        innerHeight,
        innerWidth
      } = window;
      const currentStickyBarHeight = getStickyBarHeight();
      const nextHeight = innerHeight - currentStickyBarHeight;
      setStickyBarHeight(prev => prev === currentStickyBarHeight ? prev : currentStickyBarHeight);
      setViewportHeight(prev => prev === nextHeight ? prev : nextHeight);
      setViewportWidth(prev => prev === innerWidth ? prev : innerWidth);
    };
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => {
      window.removeEventListener('resize', updateViewport);
    };
  }, []);
  useEffect(() => {
    if (isMobile) return undefined;
    const handleScroll = () => {
      const {
        scrollY,
        innerHeight
      } = window;
      const currentStickyBarHeight = getStickyBarHeight();
      const nextHeight = innerHeight - currentStickyBarHeight;
      setStickyBarHeight(prev => prev === currentStickyBarHeight ? prev : currentStickyBarHeight);
      setViewportHeight(prev => prev === nextHeight ? prev : nextHeight);
      if (!hoverRef.current) return;
      const offsetTop = hoverRef.current.getBoundingClientRect().top + scrollY;
      const insideOffsetTop = scrollY - offsetTop;
      const calcActiveBlock = Math.floor(insideOffsetTop / SINGLE_SLIDER_HEIGHT);
      const limit = blocks.length - 1;
      const nextActiveBlock = Math.min(Math.max(0, calcActiveBlock), limit);
      setActiveBlock(prev => prev === nextActiveBlock ? prev : nextActiveBlock);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [blocks.length, isMobile]);
  return <section id={code} sx={isMobile ? styles.mobileSection : styles.section(blocks.length, SINGLE_SLIDER_HEIGHT)} ref={hoverRef} data-cy={code} data-testid="product-premium-block">
      <Container sx={isMobile ? styles.mobileContainer : styles.container(viewportHeight, stickyBarHeight)} data-testid="product-premium-block-container">
        {hasViewport && (isMobile ? blocks.map((block, index) => <div key={block.itemId} sx={stackedStyles.item(viewportHeight)}>
                <ProductPremiumSlide viewportHeight={viewportHeight} viewportWidth={viewportWidth} activeBlock={0} blocks={[block]} isFirst={isFirst && index === 0} isStackedMobile />
              </div>) : <ProductPremiumSlide viewportHeight={viewportHeight} viewportWidth={viewportWidth} activeBlock={activeBlock} blocks={blocks} isFirst={isFirst} />)}
        {!isMobile && <ProductPremiumPagination blocksCount={blocks.length} activeBlock={activeBlock} />}
        {data.voxboxButton && <VoxboxWidget variant="dark" hoverRef={hoverRef} />}
      </Container>
    </section>;
};
export default ProductPremiumBlock;